/**
 * Force-End Break Modal (PR 1, Phase B1)
 *
 * Admin/TL action — manually ends an active break with a MANDATORY
 * reason. Used from the Active Breaks page.
 *
 * Backend contract: POST /api/breaks/{id}/force-end
 *   Body: { reason: string }  (≥ 5 chars, ≤ 500)
 *   Response: { ok: true, break: <updated_row> }
 *
 * Activity-log writes for this action land in PR 2 (Phase B2).
 */

import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const ForceEndBreakModal = ({ open, onOpenChange, breakRecord, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setReason('');
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return; // don't allow close mid-flight
    reset();
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      setError('Please provide a meaningful reason (at least 5 characters).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/api/breaks/${breakRecord.id}/force-end`,
        { reason: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Break force-ended successfully.', {
        description: `Duration recorded: ${resp.data.break?.duration_minutes ?? '—'} min`,
      });
      reset();
      onOpenChange(false);
      onSuccess?.(resp.data.break);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      setError(typeof detail === 'string' ? detail : 'Failed to force-end break.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!breakRecord) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="force-end-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            Force-End Break?
          </DialogTitle>
          <DialogDescription>
            This will immediately end <strong>{breakRecord.user_name}</strong>&apos;s active
            {' '}<em>{breakRecord.break_type_name}</em> break. The break will be marked as
            <strong> force-ended</strong> and flagged for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="force-end-reason" className="text-sm">
              Reason <span className="text-rose-600">*</span>
            </Label>
            <Textarea
              id="force-end-reason"
              data-testid="force-end-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Employee left for the day with break running"
              maxLength={500}
              rows={3}
              className="mt-1"
              disabled={submitting}
            />
            <p className="text-xs text-gray-400 mt-1">
              Minimum 5 characters. Stored on the break record for audit.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" data-testid="force-end-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting} data-testid="force-end-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || reason.trim().length < 5}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="force-end-confirm"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ending…</>
            ) : (
              'Force-End Break'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForceEndBreakModal;
